import { Capacitor } from '@capacitor/core';

// Conditionally import Capacitor plugins only when available
let PushNotifications, LocalNotifications;
try {
  if (Capacitor.isNativePlatform()) {
    PushNotifications = require('@capacitor/push-notifications').PushNotifications;
    LocalNotifications = require('@capacitor/local-notifications').LocalNotifications;
  }
} catch (e) {
  console.log('Capacitor notification plugins not available, running in web mode');
}

class PushNotificationService {
  constructor() {
    this.isInitialized = false;
    this.registrationToken = null;
    this.notifications = [];
    this.listeners = [];
  }

  async initialize() {
    if (this.isInitialized) {
      return;
    }

    if (!Capacitor.isNativePlatform()) {
      console.log('Push notifications are not supported on web platform');
      return;
    }

    try {
      await this.addListeners();
      await this.requestPermissions();
      this.isInitialized = true;
      console.log('Push notification service initialized successfully');
    } catch (error) {
      console.error('Failed to initialize push notification service:', error);
      throw error;
    }
  }

  async addListeners() {
    await PushNotifications.addListener('registration', (token) => {
      console.log('Push registration token:', token.value);
      this.registrationToken = token.value;
      this.notifyListeners('registration', token);
    });

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
      this.notifyListeners('registrationError', error);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      
      const formattedNotification = {
        id: notification.id || Date.now().toString(),
        title: notification.title || 'Notification',
        body: notification.body || '',
        data: notification.data || {},
        unread: true,
        timestamp: new Date().toISOString(),
        type: notification.data?.type || 'System'
      };

      this.notifications.unshift(formattedNotification);
      this.notifyListeners('notificationReceived', formattedNotification);
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action performed:', action);
      
      const notification = action.notification;
      const formattedNotification = {
        id: notification.id || Date.now().toString(),
        title: notification.title || 'Notification',
        body: notification.body || '',
        data: notification.data || {},
        unread: true,
        timestamp: new Date().toISOString(),
        type: notification.data?.type || 'System'
      };

      this.notifications.unshift(formattedNotification);
      this.notifyListeners('notificationActionPerformed', { action, notification: formattedNotification });
    });
  }

  async requestPermissions() {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      throw new Error('User denied push notification permissions');
    }

    await PushNotifications.register();
    
    // Also request local notification permissions
    if (Capacitor.isNativePlatform()) {
      await LocalNotifications.requestPermissions();
    }
    
    console.log('Push notifications registered successfully');
    return permStatus;
  }

  async checkPermissions() {
    return await PushNotifications.checkPermissions();
  }

  getRegistrationToken() {
    return this.registrationToken;
  }

  getNotifications() {
    return this.notifications;
  }

  markNotificationAsRead(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.unread = false;
      this.notifyListeners('notificationRead', notification);
    }
  }

  markNotificationAsUnread(notificationId) {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.unread = true;
      this.notifyListeners('notificationUnread', notification);
    }
  }

  clearNotifications() {
    this.notifications = [];
    this.notifyListeners('notificationsCleared');
  }

  getUnreadCount() {
    return this.notifications.filter(n => n.unread).length;
  }

  addListener(event, callback) {
    this.listeners.push({ event, callback });
  }

  removeListener(event, callback) {
    this.listeners = this.listeners.filter(
      listener => !(listener.event === event && listener.callback === callback)
    );
  }

  notifyListeners(event, data) {
    this.listeners
      .filter(listener => listener.event === event)
      .forEach(listener => listener.callback(data));
  }

  async removeAllDeliveredNotifications() {
    if (Capacitor.isNativePlatform()) {
      await PushNotifications.removeAllDeliveredNotifications();
    }
  }

  async getDeliveredNotifications() {
    if (Capacitor.isNativePlatform()) {
      return await PushNotifications.getDeliveredNotifications();
    }
    return { notifications: [] };
  }

  async scheduleTestNotification() {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Local notifications are not supported on web platform');
      return;
    }

    try {
      // Use a smaller integer ID for Android compatibility (Java int max is 2,147,483,647)
      const notificationId = Math.floor(Math.random() * 1000000);
      const now = new Date();
      const scheduledTime = new Date(now.getTime() + 5000); // 5 seconds from now

      // Clear any existing pending notifications first
      await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });

      await LocalNotifications.schedule({
        notifications: [
          {
            id: notificationId,
            title: 'Test Notification',
            body: 'This is a scheduled test notification from KOVO Home Mobile',
            schedule: { 
              at: scheduledTime,
              allowWhileIdle: true
            },
            sound: 'default',
            attachments: [],
            actionTypeId: '',
            extra: {
              type: 'Test',
              source: 'app'
            }
          }
        ]
      });

      // Get pending notifications to verify scheduling
      const pending = await LocalNotifications.getPending();
      console.log('Pending notifications:', pending);
      console.log(`Test notification scheduled for ${scheduledTime.toISOString()}`);
      console.log(`Current time: ${now.toISOString()}`);
      console.log(`Delay: 5 seconds`);
      
      return { success: true, scheduledTime, notificationId };
    } catch (error) {
      console.error('Failed to schedule test notification:', error);
      throw error;
    }
  }
}

const pushNotificationService = new PushNotificationService();
export default pushNotificationService;